import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Eraser, Layers, Wand2 } from 'lucide-react';
import {
  DESCRICAO_ESTILO_VISUAL,
  ESTILOS_VISUAIS,
  MODELOS,
  ROTULO_CATEGORIA,
  ROTULO_ESTILO_VISUAL,
  aplicarEstiloVisual,
  criarConfigPadrao,
  modelosPorCategoria,
  obterModelo,
  type ConfigPlanner,
} from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CartaoOpcao } from '@/components/CartaoOpcao';
import { useEditor } from '../EstadoEditor';
import { Grade, TituloPasso } from '../ui';

/**
 * Configuração neutra, preservando o que é do usuário.
 *
 * “Começar do zero” devolve o miolo ao ponto de partida para a pessoa seguir
 * montando na hora — sem perder o nome, os dados pessoais, as imagens enviadas
 * nem as capas prontas.
 */
function configNeutra(atual: ConfigPlanner): ConfigPlanner {
  const padrao = criarConfigPadrao();
  return {
    ...padrao,
    nome: atual.nome,
    dadosPessoais: atual.dadosPessoais,
    camadasImagem: atual.camadasImagem,
    fundos: atual.fundos,
    capas: atual.capas,
    feriados: atual.feriados,
    uf: atual.uf,
    mostrarNomesFeriados: atual.mostrarNomesFeriados,
  };
}

/**
 * Primeiro passo do assistente.
 *
 * A tela abre com “começar do zero” em cima: quem quer montar a própria agenda
 * clica e segue direto, sem passar por vinte e um modelos. Quem prefere uma
 * receita pronta vai para a área de modelos (`/modelos`), onde vê a prévia real
 * de cada um e escolhe — o modelo volta já aplicado no assistente.
 */
export function PassoModelo() {
  const { config, definirConfig, modeloInicial, avancar } = useEditor();
  const [aplicado, setAplicado] = useState<string | null>(modeloInicial ?? null);

  const modeloAplicado = aplicado ? obterModelo(aplicado) : null;

  /**
   * Devolve a configuração neutra e já segue para o passo seguinte: quem quer
   * montar do zero clica uma vez e começa a decidir tamanho, cores e o resto.
   *
   * Se o nome ainda é o do modelo aplicado, ele também volta ao padrão — não faz
   * sentido “Agenda Devocional” num miolo que acabou de ser zerado.
   */
  const comecarDoZero = () => {
    const neutra = configNeutra(config);
    const nome = modeloAplicado && config.nome === modeloAplicado.nome ? criarConfigPadrao().nome : config.nome;
    definirConfig({ ...neutra, nome });
    setAplicado(null);
    avancar();
  };

  /** Limpa cadernos e rótulos mantendo o resto da configuração atual. */
  const limparExtras = () => {
    definirConfig({
      ...config,
      cadernos: [],
      quadros: config.quadros,
      rotulosQuadros: {},
      tituloAnotacoes: '',
    });
    setAplicado(null);
  };

  const categorias = modelosPorCategoria();

  return (
    <div className="space-y-6">
      <TituloPasso
        titulo="Comece do zero ou de um modelo pronto"
        descricao="Você pode seguir montando agora, com uma configuração neutra, ou partir de um modelo que já traz formato, período, cores, tema e cadernos — e sugere os quadros do dia."
      />

      {/* Começar do zero vem primeiro: abre e já pode seguir. */}
      <section className="space-y-4 rounded-xl border border-primary/40 bg-primary/5 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-xl space-y-2">
            <Badge tom="principal">
              <Check className="size-3" />
              pronto para começar
            </Badge>
            <h3 className="text-lg font-semibold tracking-tight">Prefiro montar do zero</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Uma configuração neutra já está carregada: A5, um dia por página, de segunda a sexta,
              período do ano atual. Escolha o estilo visual abaixo (dá para trocar depois em{' '}
              <strong className="text-foreground">Tema e estilo</strong>) e clique para seguir.
            </p>
          </div>
          <Button tamanho="grande" onClick={comecarDoZero}>
            <Eraser />
            Começar do zero
            <ArrowRight />
          </Button>
        </div>

        {/*
          O estilo visual fica aqui também, e não só no passo do tema: é a
          primeira decisão de quem começa do zero, e ninguém adivinha que ela
          mora sete passos adiante.
        */}
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Estilo visual do miolo
            </h4>
            <Badge className="text-[10px]">{ROTULO_ESTILO_VISUAL[config.estiloVisual]}</Badge>
          </div>
          <Grade colunas={3}>
            {ESTILOS_VISUAIS.map((estilo) => (
              <CartaoOpcao
                key={estilo}
                selecionado={config.estiloVisual === estilo}
                titulo={ROTULO_ESTILO_VISUAL[estilo]}
                descricao={DESCRICAO_ESTILO_VISUAL[estilo]}
                aoSelecionar={() => definirConfig(aplicarEstiloVisual(config, estilo))}
              />
            ))}
          </Grade>
        </div>
      </section>

      <section className="rounded-xl border border-border bg-card p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-xl space-y-2">
            <Badge tom="neutro">
              <Wand2 className="size-3" />
              {MODELOS.length} modelos prontos
            </Badge>
            <h3 className="text-lg font-semibold tracking-tight">Quero partir de um modelo</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Os modelos ficam na área <strong className="text-foreground">Modelos prontos</strong>: você vê a
              prévia real de cada um, filtra por profissão ou assunto e escolhe. O assistente reabre com o
              modelo já aplicado, pronto para você personalizar.
            </p>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {categorias.map(({ categoria, modelos }) => (
                <span
                  key={categoria}
                  className="rounded-full border border-border px-2.5 py-0.5 text-[11px] text-muted-foreground"
                >
                  {ROTULO_CATEGORIA[categoria]} ({modelos.length})
                </span>
              ))}
            </div>
          </div>
          <Button asChild variant="contorno" tamanho="grande">
            <Link to="/modelos">
              <Layers />
              Ver modelos prontos
              <ArrowRight />
            </Link>
          </Button>
        </div>
      </section>

      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/40 p-3">
        <span className="text-xs text-muted-foreground">
          {modeloAplicado ? (
            <>
              Modelo aplicado: <strong className="text-foreground">{modeloAplicado.nome}</strong> — ajuste o que
              quiser nos próximos passos.
            </>
          ) : (
            'Sem modelo aplicado: a configuração é a que você montou.'
          )}
        </span>
        <Button
          variant="fantasma"
          tamanho="pequeno"
          className="ml-auto"
          onClick={limparExtras}
          disabled={config.cadernos.length === 0 && !config.tituloAnotacoes}
        >
          <Eraser />
          Limpar cadernos e rótulos
        </Button>
      </div>

      <p className="rounded-md border border-border bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">
        <strong className="text-foreground">Começar do zero</strong> devolve a configuração neutra, mas mantém
        seus dados pessoais, as imagens que você enviou e as capas prontas. Aplicar um modelo{' '}
        <strong className="text-foreground">substitui</strong> formato, período, cores, tema, quadros e cadernos
        — e também preserva o que é seu.
      </p>
    </div>
  );
}
